import React, { useCallback } from 'react';
import { FeatureCard, Group, TruncateByLines } from '@landr/maestro';
import { CommonCardProps, Pack, RePack } from 'types';
import { getImageUrl, getPackLinkPath } from 'helpers';
import { LinkWrapper } from 'components/Cards/BaseCard/styled';
import ExclusivePackBadge from 'components/ExclusiveCardBadge/ExclusiveCardBadge';
import { useGenericCarouselContext } from 'components/GenericCarousel';
import { useTrackingContext } from 'contexts/TrackingContext/TrackingContext';
import PlayerButton from 'components/CommonUi/PlayerButton/PlayerButton';
import { InteractionSource } from 'utils/analytics/AnalyticsV2Service.generated';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import {
    DetailedPackCardImageWrapper,
    DetailedPackCardWrapper,
    IMAGE_WIDTH_PX,
    PlayerButtonWrapper,
    Title,
    TitleWrapper,
    RelativeWrapper,
} from './DetailedPackCard.styled';

export interface DetailedPackCardProps extends CommonCardProps<Pack | RePack> {
    imageId?: string;
    page?: number;
}

const DetailedPackCard: React.FC<DetailedPackCardProps> = ({
    item,
    imageId,
    page,
    position,
    isSelected,
    setSelected,
    autoplay = true,
    ...props
}) => {
    const {
        getPackCardPlayButtonClickTracker,
        getPackPlayButtonClickTrackerV2,
    } = useTrackingContext();
    const { carouselName, carouselId } = useGenericCarouselContext();
    const toLocalizedPath = useToLocalizedPath();

    const imagePath = getImageUrl(item?.artworkUrl, IMAGE_WIDTH_PX);
    const packLinkPath = getPackLinkPath(item);
    const linkPath = toLocalizedPath(packLinkPath);

    const onPlayerButtonClick = useCallback(
        (totalTime: number) => {
            getPackCardPlayButtonClickTracker({
                carouselName,
                carouselId,
                item,
                page,
                position,
            })();

            getPackPlayButtonClickTrackerV2({
                interactionSource: InteractionSource.CardAction,
                item,
            })(totalTime);
        },
        [
            carouselName,
            carouselId,
            item,
            page,
            position,
            getPackPlayButtonClickTrackerV2,
            getPackCardPlayButtonClickTracker,
        ],
    );

    return (
        <>
            <LinkWrapper
                to={linkPath}
                data-testid="pack-card-container"
                {...props}
            >
                <DetailedPackCardWrapper>
                    <DetailedPackCardImageWrapper>
                        <RelativeWrapper>
                            <FeatureCard
                                data-testid="feature-card"
                                data-isdownloaded={item.isDownloaded}
                                data-isfree={item.isFree}
                                as={Group.Item}
                                shape="square"
                                backgroundImage={imagePath}
                                height={IMAGE_WIDTH_PX}
                                width={IMAGE_WIDTH_PX}
                                backgroundColor={(item as Pack).backgroundColor}
                                isSelected={isSelected}
                            ></FeatureCard>
                            {(item as Pack).isExclusive && (
                                <ExclusivePackBadge isAbsolute={true} />
                            )}
                        </RelativeWrapper>
                        <PlayerButtonWrapper>
                            <PlayerButton
                                id={item.guid}
                                callback={onPlayerButtonClick}
                                size="sm"
                                variant="subtle"
                                isRounded
                                isDemo
                            />
                        </PlayerButtonWrapper>
                    </DetailedPackCardImageWrapper>
                    <TitleWrapper>
                        <Title>
                            <TruncateByLines limit={2}>
                                {item.name}
                            </TruncateByLines>
                        </Title>
                    </TitleWrapper>
                </DetailedPackCardWrapper>
            </LinkWrapper>
        </>
    );
};

export default DetailedPackCard;
